import { useState, useEffect } from 'react';
import * as S from './styles';
import { ReactSVG } from 'react-svg';
import TextInput from 'components/form/textInput';

interface TableComponentProps<t> {
  data: t[] | null;
  onDelete?: (e: any, item: t) => void;
  onSelect?: (e: any, index: number | null, item: any) => void;
  defaultIndex?: number | string | null;
  setUpdateIndex?: any;
  handleOptionsChange?: any;
}

const Table = <t extends { [key: string]: any }>({
  data,
  onDelete,
  onSelect,
  defaultIndex,
  setUpdateIndex,
  handleOptionsChange
}: TableComponentProps<t>) => {
  const initialIndex =
    typeof defaultIndex === 'number'
      ? defaultIndex
      : typeof defaultIndex === 'string' && !isNaN(+defaultIndex)
        ? +defaultIndex
        : null;

  const [selectedIndex, setSelectedIndex] = useState<number | null>(initialIndex);
  const [filteredData, setFilteredData] = useState<any>();

  const handleSelect = (e: any, index: number | null, item: any) => {
    setSelectedIndex(index);
    if (onSelect) {
      onSelect(e, index, item);
    }
  };

  const handleChange = (e: any, item: any) => {
    const { name, value } = e.target;

    const updatedItems = filteredData?.map((i: any) => {
      if (
        i.translations?.[0]?.translate === item?.translations?.[0]?.translate &&
        i.translations?.[1]?.translate === item?.translations?.[1]?.translate
      ) {
        let updatedItem = { ...i };

        if (name === 'en-title') {
          updatedItem.englishOption = value;

          updatedItem.translations = updatedItem.translations.map((t: any) =>
            t.language === 'en' ? { ...t, translate: value } : t
          );
        } else if (name === 'ar-title') {
          updatedItem.arabicOption = value;

          updatedItem.translations = updatedItem.translations.map((t: any) =>
            t.language === 'ar' ? { ...t, translate: value } : t
          );
        }

        return updatedItem;
      }
      return i;
    });

    setFilteredData(updatedItems);

    if (handleOptionsChange) {
      handleOptionsChange(e, item);
    }
  };

  useEffect(() => {
    if (data) {
      setFilteredData(
        data
          ?.filter((item) => !item?.deleted)
          .map((item) => {
            const englishOption = item.translations.find((t: any) => t.language === 'en')?.translate || '';
            const arabicOption = item.translations.find((t: any) => t.language === 'ar')?.translate || '';

            return {
              ...item,
              englishOption,
              arabicOption
            };
          })
      );
    }
  }, [data]);

  useEffect(() => {
    if (initialIndex) setSelectedIndex(initialIndex);
  }, [defaultIndex, initialIndex]);

  useEffect(() => {
    const defaultIndex = filteredData?.findIndex((item: any) => item?.isDefault);
    setSelectedIndex(defaultIndex !== undefined && defaultIndex !== -1 ? defaultIndex : null);
    if (setUpdateIndex) setUpdateIndex(defaultIndex !== undefined && defaultIndex !== -1 ? defaultIndex : null);
  }, [filteredData, setSelectedIndex, setUpdateIndex]);

  return (
    <S.TableContainer>
      <S.Table>
        <thead>
          <tr>
            <th>English Options</th>
            <th>Arabic Options</th>
            {onSelect && <th style={{ textAlign: 'right', marginRight: '1.5rem' }}>Default</th>}
            {onDelete && <th />}
          </tr>
        </thead>
        <tbody>
          {filteredData?.map((row: any, index: number) => (
            <tr key={index}>
              <td style={{ width: '100%' }}>
                <TextInput
                  name="en-title"
                  value={row?.englishOption}
                  inputStyle={{ borderRadius: '8px', direction: 'ltr', textAlign: 'left' }}
                  onChange={(e) => handleChange(e, row)}
                />
              </td>
              <td style={{ width: '100%' }}>
                <TextInput
                  name="ar-title"
                  value={row?.arabicOption}
                  inputStyle={{ direction: 'rtl', textAlign: 'right', borderRadius: '8px' }}
                  onChange={(e) => handleChange(e, row)}
                />
              </td>

              {onSelect && (
                <td style={{ justifyItems: 'end', marginRight: '1.5rem' }}>
                  <S.RadioButtonWrapper isChecked={selectedIndex === index}>
                    <input
                      type="radio"
                      name="tableRadio"
                      checked={selectedIndex === index}
                      onChange={(e) => handleSelect(e, selectedIndex, row)}
                    />
                    <ReactSVG
                      className="icon"
                      src="/icons/tick.svg"
                      beforeInjection={(svg) => {
                        svg.setAttribute('width', `12px`);
                        svg.setAttribute('height', `12px`);
                      }}
                    />
                  </S.RadioButtonWrapper>
                </td>
              )}

              {onDelete && (
                <td>
                  <S.DeleteButton
                    onClick={(e) => {
                      onDelete(e, row);
                    }}
                  >
                    <ReactSVG
                      src="/icons/stroke/trash.svg"
                      beforeInjection={(svg) => {
                        svg.setAttribute('stroke', `#FF6464`);
                        svg.setAttribute('width', `18px`);
                        svg.setAttribute('height', `18px`);
                      }}
                    />
                  </S.DeleteButton>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </S.Table>
    </S.TableContainer>
  );
};

export default Table;
